import React, { useEffect, useRef, useState } from "react";
import { Button, Form, Spinner } from "react-bootstrap";
import { Helmet } from "react-helmet";
import Swal from "sweetalert2";

import { CustomBreadCrumb } from "../../../components/customBreadCrumb";
import { Loading } from "../../../components";
import { TicketApi, AppUserApi } from "../../../api";
import { TicketType } from "../../../DTO";
import { useAppNavigate, useAppParams } from "../../../Router/hook";
import { useAppSelector } from "../../../redux-config/hooks";
import { selectUser } from "../../../redux-config/entities/user";


const STATUS_COLORS: Record<number, { bg: string; text: string; label: string }> = {
    1: { bg: "#fdebed", text: "#b42335", label: "Open" },
    2: { bg: "#fff4d8", text: "#8b5b00", label: "In Progress" },
    3: { bg: "#eef1f5", text: "#687486", label: "Closed" },
    4: { bg: "#e8f1ff", text: "#215ea8", label: "Waiting — User" },
    5: { bg: "#e6f0ff", text: "#1a56b0", label: "Waiting — Support" },
};

export const TicketDetail = () => {
    const { id } = useAppParams();
    const user = useAppSelector(selectUser);
    const navigate = useAppNavigate();

    const [ticket, setTicket] = useState<TicketType>();
    const [loading, setLoading] = useState<boolean>(false);
    const [reply, setReply] = useState<string>("");
    const [sending, setSending] = useState<boolean>(false);
    const [accepting, setAccepting] = useState<boolean>(false);
    const chatEndRef = useRef<HTMLDivElement>(null);

    const crumbs = [
        { name: "", href: "" },
        { name: "Dashboard", href: "/" },
        { name: "Tickets", href: "/tickets" },
        { name: "Ticket #" + id, href: "#" },
    ];

    const fetchTicket = () => {
        if (!id) return;
        setLoading(true);
        TicketApi.ShowTicket(user, id)
            .then((data) => {
                setTicket(data.ticket);
                setLoading(false);
            })
            .catch((err) => {
                setLoading(false);
                Swal.fire({ icon: "error", text: err });
            });
    };

    useEffect(() => {
        fetchTicket();
    }, [id]);

    useEffect(() => {
        chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [ticket?.replies]);

    const handleReply = () => {
        if (!id || reply.trim().length === 0) return;
        setSending(true);
        TicketApi.ReplyTicket(user, id, reply)
            .then(() => {
                setReply("");
                setSending(false);
                fetchTicket();
            })
            .catch((err) => {
                setSending(false);
                Swal.fire({ icon: "error", text: err });
            });
    };

    const handleAcceptUser = async () => {
        if (!ticket?.user_id) return;
        const confirmed = await Swal.fire({
            title: "Accept this user?",
            text: "This will set their register_state to 'accepted' and unblock their onboarding.",
            icon: "question",
            showCancelButton: true,
            confirmButtonText: "Yes, accept",
            confirmButtonColor: "#107246",
        });
        if (!confirmed.isConfirmed) return;

        setAccepting(true);
        AppUserApi.UpdateRegisterState(user, ticket.user_id, "accepted")
            .then(() => {
                setAccepting(false);
                Swal.fire({ icon: "success", title: "User accepted", timer: 1500, showConfirmButton: false });
                fetchTicket();
            })
            .catch((err) => {
                setAccepting(false);
                Swal.fire({ icon: "error", text: err });
            });
    };

    const isRegistrationReview = ticket?.is_registration_review === true;

    const statusStyle = ticket?.status_id
        ? STATUS_COLORS[ticket.status_id] ?? STATUS_COLORS[1]
        : STATUS_COLORS[1];

    return (
        <>
            <Helmet>
                <title>D2Type | Ticket #{id}</title>
            </Helmet>
            <CustomBreadCrumb breadCrumbList={crumbs} />
            {loading && <Loading />}
            {ticket && (
                <div className="d-flex flex-column gap-3">
                    {/* Ticket header card */}
                    <div className="rounded p-3 shadow-sm bg-custom-white">
                        <div className="d-flex flex-row justify-content-between align-items-start flex-wrap gap-2">
                            <div>
                                <h5 className="mb-1">{ticket.title}</h5>
                                <span
                                    style={{
                                        background: statusStyle.bg,
                                        color: statusStyle.text,
                                        borderRadius: 999,
                                        fontSize: "0.72rem",
                                        padding: "0.2rem 0.6rem",
                                        fontWeight: 600,
                                    }}
                                >
                                    {statusStyle.label}
                                </span>
                            </div>
                            {isRegistrationReview && ticket.user_id && (
                                <Button
                                    variant="success"
                                    size="sm"
                                    disabled={accepting}
                                    onClick={handleAcceptUser}
                                >
                                    {accepting ? (
                                        <Spinner size="sm" animation="border" className="me-1" />
                                    ) : null}
                                    ✓ Accept User
                                </Button>
                            )}
                        </div>
                        <hr className="my-2" />
                        <div className="d-flex flex-row flex-wrap gap-4" style={{ fontSize: "0.85rem" }}>
                            <div>
                                <span className="text-muted">User: </span>
                                <strong
                                    style={{ cursor: ticket.user_id ? "pointer" : "default", color: "#215ea8" }}
                                    onClick={() =>
                                        ticket.user_id &&
                                        navigate(`/app-user/info/${ticket.user_id}`)
                                    }
                                >
                                    {ticket.user
                                        ? `${ticket.user.first_name ?? ""} ${ticket.user.last_name ?? ""}`.trim() ||
                                          ticket.user.email
                                        : "-"}
                                </strong>
                                {ticket.user?.register_state === "personal_rejected" && (
                                    <span
                                        style={{
                                            background: "#fdebed",
                                            color: "#b42335",
                                            borderRadius: 999,
                                            fontSize: "0.65rem",
                                            padding: "0.1rem 0.4rem",
                                            marginLeft: "0.4rem",
                                            fontWeight: 600,
                                        }}
                                    >
                                        Reg. Rejected
                                    </span>
                                )}
                            </div>
                            <div>
                                <span className="text-muted">Department: </span>
                                <strong>{ticket.department?.title ?? "-"}</strong>
                            </div>
                            {ticket.assign && (
                                <div>
                                    <span className="text-muted">Assigned to: </span>
                                    <strong>
                                        {ticket.assign.first_name} {ticket.assign.last_name}
                                    </strong>
                                </div>
                            )}
                            <div>
                                <span className="text-muted">Created: </span>
                                <strong>{ticket.created_at?.slice(0, 10)}</strong>
                            </div>
                        </div>
                        <hr className="my-2" />
                        <p className="mb-0" style={{ fontSize: "0.9rem", whiteSpace: "pre-wrap" }}>
                            {ticket.body}
                        </p>
                    </div>

                    {/* Conversation card */}
                    <div className="rounded p-3 shadow-sm bg-custom-white">
                        <h6 className="mb-3 text-muted">
                            Conversation
                            {ticket.replies && ticket.replies.length > 0 && (
                                <span className="ms-2 badge bg-secondary">{ticket.replies.length}</span>
                            )}
                        </h6>

                        <div
                            className="d-flex flex-column px-2"
                            style={{ overflowY: "auto", maxHeight: "50vh", minHeight: "120px" }}
                        >
                            {ticket.replies && ticket.replies.length > 0 ? (
                                ticket.replies.map((item) => {
                                    const isAdmin = item.support === true;
                                    const sender = item.user
                                        ? `${(item.user as any).first_name ?? ""} ${(item.user as any).last_name ?? ""}`.trim()
                                        : isAdmin
                                        ? "Support"
                                        : "User";

                                    return (
                                        <div
                                            key={item.id}
                                            className={`d-flex flex-column mb-3 ${isAdmin ? "align-items-end" : "align-items-start"}`}
                                        >
                                            <small className="text-muted mb-1 px-1">{sender}</small>
                                            <div
                                                style={{
                                                    maxWidth: "65%",
                                                    borderRadius: isAdmin
                                                        ? "16px 16px 0 16px"
                                                        : "16px 16px 16px 0",
                                                    background: isAdmin ? "#e8f1ff" : "#f5f7fc",
                                                    color: isAdmin ? "#215ea8" : "#2d3748",
                                                    padding: "0.65rem 0.9rem",
                                                }}
                                            >
                                                <p className="mb-1 text-break" style={{ fontSize: "0.9rem" }}>
                                                    {item.reply}
                                                </p>
                                                <small
                                                    className="d-block text-end"
                                                    style={{ fontSize: "0.7rem", opacity: 0.65 }}
                                                >
                                                    {item.created_at?.slice(0, 16).replace("T", " ")}
                                                </small>
                                            </div>
                                        </div>
                                    );
                                })
                            ) : (
                                <span className="text-center text-muted py-4 d-block">
                                    No replies yet.
                                </span>
                            )}
                            <div ref={chatEndRef} />
                        </div>

                        <hr className="my-2" />

                        <div className="d-flex flex-row align-items-end gap-2">
                            <Form
                                className="flex-grow-1"
                                onSubmit={(e) => e.preventDefault()}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter" && !e.shiftKey && !sending) {
                                        e.preventDefault();
                                        handleReply();
                                    }
                                }}
                            >
                                <Form.Control
                                    as="textarea"
                                    rows={2}
                                    style={{ resize: "none" }}
                                    placeholder="Type a reply… (Enter to send, Shift+Enter for newline)"
                                    value={reply}
                                    onChange={(e) => setReply(e.target.value)}
                                    disabled={sending}
                                />
                            </Form>
                            <Button
                                variant="primary"
                                disabled={sending || reply.trim().length === 0}
                                onClick={handleReply}
                                style={{ whiteSpace: "nowrap" }}
                            >
                                {sending ? <Spinner size="sm" animation="border" /> : "Send"}
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};
